import { useCallback, useEffect, useRef, useState } from 'react';
import type { IReplayEvent } from '@interfaces/IMonitorApi';

/** How long a crossed event keeps its toast and floor pulse. */
export const EVENT_SHOW_MS = 4200;

/** One shared empty list, so clearing what is already clear never re-renders. */
const NO_EVENTS: readonly IReplayEvent[] = Object.freeze([]);

export interface IActiveEvents {
  /** The events currently shown, oldest first. */
  active: readonly IReplayEvent[];
  /** Queues what a playback step crossed; it shows when the playhead next settles. */
  queue(crossed: readonly IReplayEvent[]): void;
  /** Drops what was queued and what is shown (a seek, a jump, another day, close). */
  clear(): void;
}

/**
 * The replay's crossed events: each step's crossings are queued and flushed once
 * the playhead (`playhead`) commits, then each shows for EVENT_SHOW_MS. Several
 * steps can queue before one flush, so a queue appends and never replaces.
 */
export function useActiveEvents(playhead: number | null): IActiveEvents {
  const pending = useRef<IReplayEvent[]>([]);
  const [active, setActive] = useState(NO_EVENTS);
  const timers = useRef(new Map<IReplayEvent, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const crossed = pending.current;
    pending.current = [];
    if (crossed.length === 0) return;
    for (const event of crossed) {
      clearTimeout(timers.current.get(event));
      timers.current.set(
        event,
        setTimeout(() => {
          timers.current.delete(event);
          setActive((list) => list.filter((shown) => shown !== event));
        }, EVENT_SHOW_MS),
      );
    }
    setActive((list) => [...list.filter((shown) => !crossed.includes(shown)), ...crossed]);
  }, [playhead]);

  useEffect(() => {
    const pendingTimers = timers.current;
    return () => pendingTimers.forEach(clearTimeout);
  }, []);

  const queue = useCallback((crossed: readonly IReplayEvent[]) => {
    pending.current = [...pending.current, ...crossed];
  }, []);
  const clear = useCallback(() => {
    pending.current = [];
    setActive(NO_EVENTS);
  }, []);
  return { active, queue, clear };
}
